import Icon from '@components/v2/ui/Icon/Icon';
import { TextFieldProps } from '@mui/material';
import { PickersActionBarAction } from '@mui/x-date-pickers';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Calendar, X } from 'lucide-react';
import { forwardRef } from 'react';
import { makeStyles } from 'tss-react/mui';

const COMPACT_DAY_SIZE = 28;
const COMPACT_CALENDAR_HEIGHT = 264;

const useStyles = makeStyles()((theme) => ({
  dialog: {
    '& .MuiDialogActions-spacing > :first-child': {
      color: `${theme.palette.text.secondary} !important`,
    },
  },
  compactPopup: {
    '& .MuiDateCalendar-root': {
      width: 248,
      maxHeight: COMPACT_CALENDAR_HEIGHT,
    },
    '& .MuiPickersCalendarHeader-root': {
      minHeight: 32,
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(0.5),
    },
    '& .MuiPickersCalendarHeader-label': {
      fontSize: theme.typography.body2.fontSize,
    },
    '& .MuiDayCalendar-slideTransition': {
      minHeight: 180,
    },
    '& .MuiPickersDay-root, & .MuiDayCalendar-weekDayLabel': {
      width: COMPACT_DAY_SIZE,
      height: COMPACT_DAY_SIZE,
      margin: '0 1px',
      fontSize: theme.typography.caption.fontSize,
    },
    '& .MuiYearCalendar-root, & .MuiMonthCalendar-root': {
      width: 248,
      maxHeight: COMPACT_CALENDAR_HEIGHT,
    },
    '& .MuiMultiSectionDigitalClock-root': {
      height: COMPACT_CALENDAR_HEIGHT,
      maxHeight: COMPACT_CALENDAR_HEIGHT,
    },
    '& .MuiMultiSectionDigitalClockSection-root': {
      width: 48,
      height: COMPACT_CALENDAR_HEIGHT,
      maxHeight: COMPACT_CALENDAR_HEIGHT,
    },
    '& .MuiMultiSectionDigitalClockSection-item': {
      minHeight: COMPACT_DAY_SIZE,
      fontSize: theme.typography.caption.fontSize,
    },
  },
}));

const OpenPickerIcon = () => <Icon icon={Calendar} size="md" />;

const ClearIcon = () => <Icon icon={X} size="md" />;

type Props = {
  name?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
  disablePast?: boolean;
  minDate?: Date;
  maxDate?: Date;
  minDateTime?: Date;
  maxDateTime?: Date;
  value?: Date | null;
  size?: Extract<TextFieldProps['size'], 'small' | 'medium'>;
  popupSize?: 'compact' | 'default';
  open?: boolean;
  onBlur?: TextFieldProps['onBlur'];
  onKeyDown?: TextFieldProps['onKeyDown'];
  onChange?: (date: Date | null) => void;
  onAccept?: (date: Date | null) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

const DateTimePicker = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label,
      error,
      helperText,
      disabled,
      required,
      clearable = true,
      fullWidth = true,
      autoFocus,
      disablePast,
      minDate,
      maxDate,
      minDateTime,
      maxDateTime,
      value,
      size = 'medium',
      popupSize = 'default',
      open,
      onBlur,
      onKeyDown,
      onChange,
      onAccept,
      onOpen,
      onClose,
    },
    ref,
  ) => {
    const { classes } = useStyles();

    const actions: PickersActionBarAction[] = clearable ? ['clear', 'accept'] : ['accept'];

    return (
      <MuiDateTimePicker
        name={name}
        label={label}
        value={value}
        inputRef={ref}
        minDate={minDate}
        maxDate={maxDate}
        minDateTime={minDateTime}
        maxDateTime={maxDateTime}
        disabled={disabled}
        disablePast={disablePast}
        open={open}
        onChange={(date) => onChange?.(date)}
        onAccept={(date) => onAccept?.(date)}
        onOpen={onOpen}
        onClose={onClose}
        slots={{ openPickerIcon: OpenPickerIcon, clearIcon: ClearIcon }}
        slotProps={{
          openPickerButton: { edge: false },
          dialog: { className: classes.dialog },
          actionBar: { actions },
          desktopPaper: {
            className: popupSize === 'compact' ? classes.compactPopup : undefined,
          },
          field: {
            clearable,
            onClear: () => onChange?.(null),
          },
          textField: {
            name,
            size,
            error,
            onBlur,
            onKeyDown,
            required,
            autoFocus,
            fullWidth,
            helperText,
            InputLabelProps: { shrink: true },
          },
        }}
      />
    );
  },
);

DateTimePicker.displayName = 'DateTimePicker';
export type { Props as DateTimePickerProps };
export default DateTimePicker;
